import { Component, OnInit } from '@angular/core';
import { GlobalService } from 'src/app/Services/global.service';

interface CarouselItem {
  bunnyVideoId: string;
  title: string;
  description: string;
  tags: string;
  active: boolean;
}

@Component({
  selector: 'app-web-media',
  templateUrl: './web-media.component.html',
  styleUrls: ['./web-media.component.scss']
})
export class WebMediaComponent implements OnInit {
  loading = false;
  saving = false;
  submitted = false;
  errorMessage = '';
  selectedBanner: File | null = null;
  bannerPreview = '';
  removeBanner = false;

  dashboardVideoId = '';
  faqHeroVideoId = '';
  faqBannerImage = '';
  carouselItems: CarouselItem[] = [];

  constructor(public service: GlobalService) {}

  ngOnInit(): void {
    this.loadMedia();
  }

  loadMedia(): void {
    this.loading = true;
    this.errorMessage = '';
    this.service.GetApi('/web-media').subscribe({
      next: (response: any) => {
        if (response?.success === 1) {
          const media = response.data || {};
          this.dashboardVideoId = media.dashboardVideoId || '';
          this.faqHeroVideoId = media.faqHeroVideoId || '';
          this.faqBannerImage = media.faqBannerImage || '';
          this.bannerPreview = this.faqBannerImage;
          const configuredItems = (media.carouselItems || []).slice(0, 6).map((item: any) => this.normaliseItem(item));
          // Keep three reusable starter slots available even before the final Bunny IDs are known.
          this.carouselItems = configuredItems.length ? configuredItems : [this.emptyItem(), this.emptyItem(), this.emptyItem()];
          this.loading = false;
          return;
        }
        this.errorMessage = response?.message || 'Unable to load media configuration.';
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load media configuration. Please try again.';
        this.loading = false;
      }
    });
  }

  private normaliseItem(item: any): CarouselItem {
    return {
      bunnyVideoId: item?.bunnyVideoId || item?.guid || '',
      title: item?.title || '',
      description: item?.description || '',
      tags: Array.isArray(item?.tags) ? item.tags.join(', ') : (item?.tags || ''),
      active: item?.active !== false
    };
  }

  private emptyItem(): CarouselItem {
    return { bunnyVideoId: '', title: '', description: '', tags: '', active: true };
  }

  addCarouselItem(): void {
    if (this.carouselItems.length >= 6) return;
    this.carouselItems.push({ bunnyVideoId: '', title: '', description: '', tags: '', active: true });
  }

  removeCarouselItem(index: number): void {
    this.carouselItems.splice(index, 1);
  }

  moveCarouselItem(index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (target < 0 || target >= this.carouselItems.length) return;
    const item = this.carouselItems[index];
    this.carouselItems[index] = this.carouselItems[target];
    this.carouselItems[target] = item;
  }

  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.selectedBanner = null;
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      this.service.dangerAlert('Please select a JPG, PNG, WEBP, or GIF image.');
      input.value = '';
      return;
    }
    this.selectedBanner = file;
    this.removeBanner = false;
    const reader = new FileReader();
    reader.onload = () => this.bannerPreview = String(reader.result || '');
    reader.readAsDataURL(file);
  }

  clearBanner(): void {
    this.selectedBanner = null;
    this.bannerPreview = '';
    this.removeBanner = true;
  }

  save(): void {
    this.submitted = true;
    this.errorMessage = '';
    const data = new FormData();
    data.append('dashboardVideoId', this.dashboardVideoId.trim());
    data.append('faqHeroVideoId', this.faqHeroVideoId.trim());
    data.append('carouselItems', JSON.stringify(this.carouselItems.map((item, index) => ({
      bunnyVideoId: item.bunnyVideoId.trim(),
      title: item.title.trim(),
      description: item.description.trim(),
      tags: item.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      displayOrder: index,
      active: item.active
    }))));
    if (this.selectedBanner) data.append('bannerImage', this.selectedBanner, this.selectedBanner.name);
    if (this.removeBanner) data.append('removeBannerImage', 'true');

    this.saving = true;
    this.service.updateFormData(data, '/web-media').subscribe({
      next: (response: any) => {
        this.saving = false;
        if (response?.success === 1) {
          this.submitted = false;
          this.selectedBanner = null;
          this.removeBanner = false;
          this.faqBannerImage = response.data?.faqBannerImage || '';
          this.bannerPreview = this.faqBannerImage;
          this.service.successAlert(response.message || 'Media configuration updated successfully.');
        } else {
          this.errorMessage = response?.message || 'Unable to save media configuration.';
        }
      },
      error: () => {
        this.saving = false;
        this.errorMessage = 'Unable to save media configuration. Please try again.';
      }
    });
  }
}
